import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Phone, MessageSquare, Eye, XCircle, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';
import { membersAPI } from '../services/apiService';
import ActionMenu from './ActionMenu';
import PortalsAccessModal from './PortalsAccessModal';
import MemberKycPreview from './MemberKycPreview';
import chatService from '../services/chatService';
import {
  FACTOR_STATUS_FIELD,
  getActiveCaseFactors,
  resolveClaimFactorValues
} from '../services/pricingService';
import { isInstanceTrackedFactor } from '../utils/factorInstances';

const MEMBERS_CACHE_KEY = 'pf_members_cache_v3';

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'Not Started', label: 'Not Started' },
  { id: 'Work in Progress', label: 'In Progress' },
  { id: 'Completed', label: 'Completed' },
  { id: 'No claims', label: 'No claims' }
];

/** Outlined tablet badges — colored border, transparent fill */
const outlinedStatusClass = (status, active = false) => {
  const base = 'inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border bg-transparent transition';
  const map = {
    all: active
      ? 'border-primary-600 text-primary-700 ring-1 ring-primary-200'
      : 'border-slate-300 text-slate-600 hover:border-slate-400',
    Completed: active
      ? 'border-green-600 text-green-700 ring-1 ring-green-200'
      : 'border-green-500 text-green-700 hover:border-green-600',
    'Work in Progress': active
      ? 'border-amber-500 text-amber-800 ring-1 ring-amber-200'
      : 'border-amber-400 text-amber-700 hover:border-amber-500',
    'Not Started': active
      ? 'border-slate-500 text-slate-800 ring-1 ring-slate-200'
      : 'border-slate-400 text-slate-700 hover:border-slate-500',
    'No claims': active
      ? 'border-rose-400 text-rose-700 ring-1 ring-rose-200'
      : 'border-rose-300 text-rose-600 hover:border-rose-400'
  };
  return `${base} ${map[status] || map['Not Started']}`;
};

const shortStatusLabel = (status) => {
  if (status === 'Work in Progress' || status === 'In Progress') return 'In Progress';
  if (status === 'Pending with Employer/PF Office') return 'Pending (Employer/PF)';
  return status || 'Not Started';
};

const statusDotClass = (status) => {
  const s = status || 'Not Started';
  if (s === 'Completed') return 'bg-green-500';
  if (s === 'Pending with Employer/PF Office') return 'bg-orange-500';
  if (s === 'Work in Progress' || s === 'In Progress') return 'bg-amber-500';
  return 'bg-slate-400';
};

const claimMatchesBucket = (claim, bucket) => {
  const fs = claim?.final_status || 'Not Started';
  if (bucket === 'Completed') return fs === 'Completed';
  if (bucket === 'Work in Progress') {
    return ['Work in Progress', 'In Progress', 'Pending with Employer/PF Office'].includes(fs);
  }
  if (bucket === 'Not Started') return !fs || fs === '' || fs === 'Not Started';
  return true;
};

/** Activity lines for one claim (parent + multi-instance when present) */
const buildClaimActivityLines = (claim) => {
  const values = resolveClaimFactorValues(claim);
  const active = getActiveCaseFactors(values);
  const instancesByKey = claim?.pricing_data?.factorInstances || {};
  const lines = [];

  active.forEach((factor) => {
    const field = FACTOR_STATUS_FIELD[factor.key];
    const parentStatus = (field && claim[field]) || 'Not Started';
    const instList = instancesByKey[factor.key] || [];

    if (isInstanceTrackedFactor(factor.key) && instList.length > 0) {
      instList.forEach((inst) => {
        const name =
          String(inst.label || '').trim() ||
          String(inst.company_name || '').trim() ||
          String(inst.member_id || '').trim() ||
          String(inst.key || '').trim() ||
          'Item';
        lines.push({
          label: `${factor.label} · ${name}`,
          status: inst.status || 'Not Started'
        });
      });
    } else {
      lines.push({ label: factor.label, status: parentStatus });
    }
  });

  return lines;
};

const TIP_WIDTH = 320;
const TIP_GAP = 6;
const TIP_PAD = 8;
const TIP_MAX_H = 280;

/** Prefer below → above → right → left; clamp into viewport */
const computeTipPlacement = (anchorRect, tipWidth, tipHeight) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const br = anchorRect;
  const w = Math.min(tipWidth || TIP_WIDTH, vw - TIP_PAD * 2);
  const hEst = Math.min(tipHeight || TIP_MAX_H, TIP_MAX_H);

  const space = {
    below: vh - br.bottom - TIP_PAD - TIP_GAP,
    above: br.top - TIP_PAD - TIP_GAP,
    right: vw - br.right - TIP_PAD - TIP_GAP,
    left: br.left - TIP_PAD - TIP_GAP
  };

  const clampLeft = (left) => Math.max(TIP_PAD, Math.min(left, vw - w - TIP_PAD));
  const clampTop = (top, height) => Math.max(TIP_PAD, Math.min(top, vh - height - TIP_PAD));

  const options = [
    {
      id: 'below',
      fits: space.below >= Math.min(hEst, 140),
      score: space.below,
      top: br.bottom + TIP_GAP,
      left: clampLeft(br.left),
      maxHeight: Math.min(TIP_MAX_H, Math.max(100, space.below))
    },
    {
      id: 'above',
      fits: space.above >= Math.min(hEst, 140),
      score: space.above,
      top: br.top - TIP_GAP - hEst,
      left: clampLeft(br.left),
      maxHeight: Math.min(TIP_MAX_H, Math.max(100, space.above))
    },
    {
      id: 'right',
      fits: space.right >= w * 0.85,
      score: space.right,
      top: clampTop(br.top, Math.min(hEst, TIP_MAX_H)),
      left: br.right + TIP_GAP,
      maxHeight: Math.min(TIP_MAX_H, Math.max(100, vh - TIP_PAD * 2))
    },
    {
      id: 'left',
      fits: space.left >= w * 0.85,
      score: space.left,
      top: clampTop(br.top, Math.min(hEst, TIP_MAX_H)),
      left: br.left - TIP_GAP - w,
      maxHeight: Math.min(TIP_MAX_H, Math.max(100, vh - TIP_PAD * 2))
    }
  ];

  const fitting = options.filter((o) => o.fits);
  const chosen = (fitting.length ? fitting : options).sort((a, b) => b.score - a.score)[0];

  let { top, left, maxHeight, id } = chosen;
  left = clampLeft(left);

  if (id === 'above') {
    top = br.top - TIP_GAP - Math.min(hEst, maxHeight);
  }
  top = clampTop(top, Math.min(hEst, maxHeight));
  maxHeight = Math.min(maxHeight, vh - top - TIP_PAD);

  return { top, left, maxHeight: Math.max(100, maxHeight), width: w, placement: id };
};

const ClaimStatusHover = ({ member, status, count }) => {
  const badgeRef = useRef(null);
  const tipRef = useRef(null);
  const closeTimer = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({
    top: 0,
    left: 0,
    maxHeight: TIP_MAX_H,
    width: TIP_WIDTH,
    ready: false
  });
  const claims = (member.claims_preview || []).filter((c) => claimMatchesBucket(c, status));
  const displayName = status === 'Work in Progress' ? 'In Progress' : status;

  const clearClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const placeTip = useCallback(() => {
    const badge = badgeRef.current;
    const tip = tipRef.current;
    if (!badge) return;

    const br = badge.getBoundingClientRect();
    const measuredW = tip?.offsetWidth || TIP_WIDTH;
    const measuredH = tip?.offsetHeight || TIP_MAX_H;
    const next = computeTipPlacement(br, measuredW, measuredH);

    if (tip && next.placement === 'above') {
      const h = Math.min(tip.scrollHeight || measuredH, next.maxHeight);
      next.top = Math.max(TIP_PAD, br.top - TIP_GAP - h);
      next.maxHeight = Math.min(next.maxHeight, Math.max(100, br.top - TIP_PAD - TIP_GAP));
    }

    const nextPos = {
      top: Math.round(next.top),
      left: Math.round(next.left),
      maxHeight: Math.round(next.maxHeight),
      width: Math.round(next.width),
      ready: true
    };

    setPos((prev) => {
      if (
        prev.ready &&
        prev.top === nextPos.top &&
        prev.left === nextPos.left &&
        prev.maxHeight === nextPos.maxHeight &&
        prev.width === nextPos.width
      ) {
        return prev;
      }
      return nextPos;
    });
  }, []);

  const showTip = () => {
    clearClose();
    setOpen(true);
  };

  const hideTip = () => {
    clearClose();
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      setPos((p) => ({ ...p, ready: false }));
    }, 120);
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    placeTip();
    const raf = requestAnimationFrame(() => placeTip());
    const onMove = () => placeTip();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, placeTip]);

  useEffect(() => () => clearClose(), []);

  return (
    <>
      <span
        ref={badgeRef}
        className={`${outlinedStatusClass(status)} cursor-default`}
        onMouseEnter={showTip}
        onMouseLeave={hideTip}
      >
        {displayName}
        <span className="ml-1 opacity-80">({count})</span>
      </span>
      {open &&
        createPortal(
          <div
            ref={tipRef}
            className="fixed z-[80] rounded-lg border border-slate-200 bg-white p-2.5 text-left shadow-lg"
            style={{
              top: pos.top,
              left: pos.left,
              width: pos.width,
              maxWidth: `min(${TIP_WIDTH}px, calc(100vw - ${TIP_PAD * 2}px))`,
              maxHeight: pos.maxHeight,
              overflowY: 'auto',
              visibility: pos.ready ? 'visible' : 'hidden',
              pointerEvents: pos.ready ? 'auto' : 'none'
            }}
            onMouseEnter={showTip}
            onMouseLeave={hideTip}
          >
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {count} claim{count === 1 ? '' : 's'} · {displayName}
            </div>
            {claims.length === 0 ? (
              <p className="text-xs text-slate-600">No activity details available yet.</p>
            ) : (
              <div className="space-y-2">
                {claims.map((claim) => {
                  const lines = buildClaimActivityLines(claim);
                  const done = lines.filter((l) => l.status === 'Completed').length;
                  return (
                    <div key={claim.id} className="rounded-md bg-slate-50 px-2 py-1.5">
                      <div className="mb-1 flex items-center justify-between gap-2 text-xs font-medium text-slate-800">
                        <span>Claim #{claim.id}</span>
                        {lines.length > 0 && (
                          <span className="text-[10px] font-normal text-slate-500">
                            {done}/{lines.length} done
                          </span>
                        )}
                      </div>
                      {lines.length === 0 ? (
                        <p className="text-[11px] text-slate-500">No activities in scope.</p>
                      ) : (
                        <ul className="space-y-1">
                          {lines.map((line) => (
                            <li
                              key={`${claim.id}-${line.label}`}
                              className="flex items-start gap-1.5 text-[11px] leading-snug text-slate-700"
                            >
                              <span
                                className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${statusDotClass(line.status)}`}
                              />
                              <span className="min-w-0 flex-1 break-words">{line.label}</span>
                              <span className="shrink-0 font-medium text-slate-600">
                                {shortStatusLabel(line.status)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

const memberMatchesStatus = (member, statusFilter) => {
  if (!statusFilter || statusFilter === 'all') return true;
  const claimCount = Number(member.claim_count) || 0;
  if (statusFilter === 'No claims') return claimCount === 0;
  if (statusFilter === 'Completed') return (Number(member.completed_count) || 0) > 0;
  if (statusFilter === 'Work in Progress') return (Number(member.in_progress_count) || 0) > 0;
  if (statusFilter === 'Not Started') return (Number(member.not_started_count) || 0) > 0;
  return true;
};

const MembersList = () => {
  const navigate = useNavigate();
  const cached = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(MEMBERS_CACHE_KEY) || 'null');
    } catch {
      return null;
    }
  })();
  const [members, setMembers] = useState(() => (Array.isArray(cached) ? cached : []));
  const [loading, setLoading] = useState(!cached?.length);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filteredMembers, setFilteredMembers] = useState(() => (Array.isArray(cached) ? cached : []));
  const [portalsMember, setPortalsMember] = useState(null);
  const [kycPreviewMember, setKycPreviewMember] = useState(null);
  const [chatUnreadByMember, setChatUnreadByMember] = useState({});

  const loadChatUnread = useCallback(async () => {
    try {
      const counts = await chatService.getUnreadCountsByMember();
      setChatUnreadByMember(counts || {});
    } catch (error) {
      console.error('Error loading chat unread counts:', error);
    }
  }, []);

  const fetchMembers = async ({ silent = false } = {}) => {
    if (!silent && !members.length) setLoading(true);
    else setRefreshing(true);
    try {
      const membersData = await membersAPI.getAll();
      setMembers(membersData);
      sessionStorage.setItem(MEMBERS_CACHE_KEY, JSON.stringify(membersData));
      sessionStorage.removeItem('pf_members_cache_v2');
      sessionStorage.removeItem('pf_members_cache');
      loadChatUnread();
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const filterMembers = useCallback(() => {
    const q = searchTerm.toLowerCase();
    const filtered = members.filter((member) => {
      const matchesSearch = !q
        || member.name?.toLowerCase().includes(q)
        || member.uan?.toLowerCase().includes(q)
        || member.referred_by?.toLowerCase().includes(q);
      return matchesSearch && memberMatchesStatus(member, statusFilter);
    });
    setFilteredMembers(filtered);
  }, [members, searchTerm, statusFilter]);

  useEffect(() => {
    fetchMembers({ silent: Boolean(cached?.length) });
    loadChatUnread();
    const interval = setInterval(loadChatUnread, 5000);
    const refresh = () => loadChatUnread();
    window.addEventListener('newChatMessage', refresh);
    window.addEventListener('chatReadUpdated', refresh);
    window.addEventListener('adminReplyAdded', refresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('newChatMessage', refresh);
      window.removeEventListener('chatReadUpdated', refresh);
      window.removeEventListener('adminReplyAdded', refresh);
    };
  }, []);

  useEffect(() => {
    filterMembers();
  }, [filterMembers]);

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete member "${name}"? This will also delete all associated claims.`)) {
      try {
        await membersAPI.delete(id);
        toast.success('Member deleted successfully');
        fetchMembers();
      } catch (error) {
        console.error('Error deleting member:', error);
      }
    }
  };

  const handleToggleInactive = async (member) => {
    try {
      const updatedMember = {
        ...member,
        is_inactive: !member.is_inactive
      };
      await membersAPI.update(member.id, updatedMember);
      toast.success(`Member ${updatedMember.is_inactive ? 'deactivated' : 'activated'} successfully`);
      fetchMembers();
    } catch (error) {
      console.error('Error updating member status:', error);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '-';
    const num = phone.toString();
    if (num.includes('E+')) return num;
    return num.length === 12 ? `+${num}` : num;
  };

  const getClaimStatusBadges = (member) => {
    const count = Number(member.claim_count) || 0;
    if (count === 0) {
      return <span className={outlinedStatusClass('No claims')}>No claims</span>;
    }

    const parts = [
      { status: 'Completed', count: Number(member.completed_count) || 0 },
      { status: 'Work in Progress', count: Number(member.in_progress_count) || 0 },
      { status: 'Not Started', count: Number(member.not_started_count) || 0 }
    ].filter((p) => p.count > 0);

    if (parts.length === 0) {
      return <span className={outlinedStatusClass('Not Started')}>{count} claim(s)</span>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {parts.map(({ status, count: c }) => (
          <ClaimStatusHover key={status} member={member} status={status} count={c} />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h1 className="page-title">Members</h1>
          <p className="page-subtitle">
            Manage PF members and their claims
            {refreshing ? <span className="ml-2 text-primary-600">Refreshing…</span> : null}
          </p>
        </div>
        <Link
          to="/adminportal/members/new"
          className="btn-primary inline-flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Member
        </Link>
      </div>

      <div className="card-compact space-y-3">
        <input
          type="text"
          placeholder="Search by name, UAN, or referred by..."
          className="input-field"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="text-xs text-slate-500 mr-1">Claim status</span>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setStatusFilter(f.id)}
              className={outlinedStatusClass(f.id === 'all' ? 'all' : f.id, statusFilter === f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card-compact overflow-hidden !p-0">
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="table-header">Sl No</th>
                <th className="table-header">Name</th>
                <th className="table-header">UAN</th>
                <th className="table-header">UAN Password</th>
                <th className="table-header">Referred By</th>
                <th className="table-header">Phone</th>
                <th className="table-header">Claim Status</th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="table-cell text-center py-8 text-slate-500">
                    {searchTerm || statusFilter !== 'all'
                      ? 'No members found matching your filters.'
                      : 'No members found. Add your first member to get started.'}
                  </td>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <tr key={member.id} className={`hover:bg-slate-50/80 ${member.is_inactive ? 'opacity-60' : ''}`}>
                    <td className="table-cell font-medium">{member.sl_no || '-'}</td>
                    <td className="table-cell">
                      <div className="font-medium text-slate-900">{member.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Portal: {member.password || '—'}
                      </div>
                    </td>
                    <td className="table-cell font-mono text-sm">{member.uan}</td>
                    <td className="table-cell">
                      <span className="text-sm text-slate-600">{member.uan_password || 'N/A'}</span>
                    </td>
                    <td className="table-cell">{member.referred_by || '-'}</td>
                    <td className="table-cell">
                      <div className="flex items-center space-x-2">
                        {member.phone_number && (
                          <a
                            href={`tel:${member.phone_number}`}
                            className="text-primary-600 hover:text-primary-700"
                            title="Call"
                          >
                            <Phone className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            window.dispatchEvent(new CustomEvent('openMemberChat', {
                              detail: { memberId: member.id, memberName: member.name, memberUan: member.uan }
                            }));
                          }}
                          className="relative text-primary-600 hover:text-primary-700 p-0.5"
                          title="Chat with Member"
                        >
                          <MessageSquare className="w-4 h-4" />
                          {Number(chatUnreadByMember[member.id]) > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 text-[11px] font-semibold text-emerald-600 leading-none tabular-nums">
                              {Number(chatUnreadByMember[member.id]) > 99
                                ? '99+'
                                : chatUnreadByMember[member.id]}
                            </span>
                          )}
                        </button>
                        <span className="text-sm">{formatPhoneNumber(member.phone_number)}</span>
                      </div>
                    </td>
                    <td className="table-cell">
                      {getClaimStatusBadges(member)}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setKycPreviewMember(member)}
                          className={`p-1.5 rounded-md transition ${
                            member.has_kyc_docs
                              ? 'text-primary-600 hover:bg-primary-50'
                              : 'text-slate-300 hover:text-slate-400'
                          }`}
                          title={member.has_kyc_docs ? 'View KYC documents (merged PDF)' : 'View KYC (may be empty)'}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <ActionMenu
                        items={[
                          {
                            label: 'Edit Member',
                            icon: Edit,
                            onClick: () => navigate(`/adminportal/members/${member.id}/edit`)
                          },
                          {
                            label: 'Manage Claim',
                            icon: Eye,
                            onClick: () => navigate(`/adminportal/members/${member.id}`)
                          },
                          {
                            label: 'Portals Access',
                            icon: KeyRound,
                            onClick: () => setPortalsMember(member)
                          },
                          {
                            label: member.is_inactive ? 'Activate' : 'Mark Inactive',
                            icon: XCircle,
                            onClick: () => handleToggleInactive(member)
                          },
                          {
                            label: 'Delete',
                            icon: Trash2,
                            danger: true,
                            onClick: () => handleDelete(member.id, member.name)
                          }
                        ]}
                      />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-sm text-slate-500 text-center">
        Showing {filteredMembers.length} of {members.length} members
      </div>

      {portalsMember && (
        <PortalsAccessModal
          member={portalsMember}
          onClose={() => setPortalsMember(null)}
        />
      )}

      {kycPreviewMember && (
        <MemberKycPreview
          memberId={kycPreviewMember.id}
          memberName={kycPreviewMember.name}
          onClose={() => setKycPreviewMember(null)}
        />
      )}
    </div>
  );
};

export default MembersList;
